// Modules
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, FormGroup, InputGroup } from '@blueprintjs/core'

// Components
import Logo from 'components/brand/logo/Logo'
import Page from 'components/layout/page/Page'
import ProviderButton from 'components/external/provider-button/ProviderButton'

// Hooks
import useLanguage from 'hooks/system/useLanguage'
import useLogin from 'hooks/authentication/useLogin'

// Constants
import { SSO_PROVIDERS } from 'constants/environment'

// Styles
import './Form.scss'

// Section: Login > Landing > Form
function Form () {
	// References
	const form = useRef()

	// State
	const [formData, setFormData] = useState({ token: '', email: '', password: '' })
	const [isInitialized, setIsInitialized] = useState(false)
	const [isFormReady, setIsFormReady] = useState(false)
	const [stage, setStage] = useState('credentials') // STAGES: 1. credentials, 2. twoFactorAuthentication

	// Data
	const { translate } = useLanguage('pages.login.views.landing.sections.form')
	const { isExpectingCode, isProcessing, login } = useLogin()

	// Functions
	function handleInput (type, value) {
		setFormData(formData => {
			const newData = { ...formData, [type]: value }
			setIsFormReady(newData.email.length > 0 && newData.password.length > 0) // TODO: Improve email and password check
			return newData
		})
	}
	function handleSubmit (event) {
		event.preventDefault()
		if (isFormReady) login(formData)
	}

	// Functions (Memoized)
	const onResponse = useCallback((response, provider) => login(response, provider), [login])

	// Effects
	useEffect(() => {
		setTimeout(() => {
			setIsInitialized(true)
			const isFilled = (
				Array
					.from(form.current?.querySelectorAll('input'))
					?.every(input => input.matches(':-internal-autofill-selected'))
			)
			if (isFilled) setIsFormReady(true)
		}, 1000)
	}, [])
	useEffect(() => {
		if (isExpectingCode) setStage('twoFactorAuthentication')
	}, [isExpectingCode])

	// Render
	return (
		<Page.Section
			willGrow
			willCenter
			name="form">
			<div className={
				'aix-authentication-form' +
				(isInitialized ? ' aix-initialized' : '')
			}>
				<Logo className="aix-logo" />
				<div className="aix-content">
					<form
						ref={form}
						className="aix-form aix-paper-thick">
						<h2 className="aix-title">{translate.local(`form.title.${stage}`)}</h2>
						{stage === 'credentials' &&
							<>
								<FormGroup>
									<InputGroup
										disabled={isProcessing}
										name="email"
										type="email"
										value={formData.email}
										className="aix-input aix-email"
										placeholder={translate.local('form.fields.email.placeholder')}
										onChange={({ target }) => handleInput('email', target.value)} />
								</FormGroup>
								<FormGroup>
									<InputGroup
										disabled={isProcessing}
										name="password"
										type="password"
										value={formData.password}
										className="aix-input aix-password"
										placeholder={translate.local('form.fields.password.placeholder')}
										onChange={({ target }) => handleInput('password', target.value)} />
									<div className="aix-option">
										<Link to="/reset">{translate.local('form.options.reset.action')}</Link>
									</div>
								</FormGroup>
							</>
						}
						{stage === 'twoFactorAuthentication' &&
							<FormGroup>
								<InputGroup
									disabled={isProcessing}
									name="code"
									type="text"
									value={formData.token}
									className="aix-input aix-code"
									placeholder={translate.local('form.fields.code.placeholder')}
									onChange={({ target }) => handleInput('token', target.value)} />
							</FormGroup>
						}
						<Button
							fill
							disabled={!isFormReady || isProcessing}
							loading={isProcessing}
							className="aix-submit bp4-large"
							onClick={handleSubmit}
							type="submit">
							{translate.local('form.actions.login.label')}
						</Button>
					</form>
					{stage === 'credentials' &&
						<>
							{SSO_PROVIDERS?.length > 0 &&
								<ul className="aix-actions">
									{SSO_PROVIDERS.map(provider => (
										<li
											key={provider}
											className="aix-action">
											<ProviderButton
												type="continue"
												provider={provider}
												isDisabled={isProcessing}
												isInitialized={isInitialized}
												onResponse={onResponse} />
										</li>
									))}
								</ul>
							}
							<small className="aix-options">{translate.local('form.options.register.label')} <Link to="/register">{translate.local('form.options.register.action')}</Link></small>
						</>
					}
				</div>
			</div>
		</Page.Section>
	)
}

// Export
export default Form
